import { PRICING_ATTRIBUTES } from '@amzn/journeys-common';
import { Link, Text } from '@amzn/meridian';
import { STATUSES, Status } from 'components/Status/Status';
import type { ReactNode } from 'react';

type PriceStructure = {
  value:
    | string
    | {
        S: string;
      };
  currency:
    | string
    | {
        S: string;
      };
  [key: string]: unknown;
};

type AsinLinkValue = {
  asin: string;
  marketplace: string;
};

type ValueType = unknown;

const wrapStyles = {
  display: 'block',
  maxWidth: '100%',
  overflowWrap: 'break-word' as const,
  tableLayout: 'fixed' as const,
  whiteSpace: 'normal' as const,
  wordBreak: 'break-word' as const,
};

// checks if a string matches language header pattern (e.g., "tr_DE" or "fr_BE")
const isLanguageHeader = (line: string): boolean => /^[A-Z]{2}_[A-Z]{2}$/.test(line);

// type guard to check if an object is a price-type attribute structure by checking for required properties
const isPriceStructure = (obj: unknown): obj is PriceStructure =>
  obj !== null && typeof obj === 'object' && 'value' in obj && 'currency' in obj;

// biome-ignore lint/suspicious/noExplicitAny: legacy code
const getObjectValue = (field: any): string => (field instanceof Object ? field.S : field);

const INTERNAL_FIELDS = [
  '__typename',
  'uiId',
] as const;

const filterInternalFields = ([key]: [
  string,
  unknown,
]): boolean => !INTERNAL_FIELDS.includes(key as (typeof INTERNAL_FIELDS)[number]);

const formatPriceStructure = (value: PriceStructure): string => {
  const amount = getObjectValue(value.value);
  const currency = getObjectValue(value.currency);
  return `${amount} ${currency}`;
};

const formatLanguageTaggedValues = (
  values: Array<{
    value: string;
  }>,
): string => values.map((item) => item.value).join('\n');

const formatObjectEntries = (
  entries: [
    string,
    unknown,
  ][],
): string =>
  entries
    .filter(filterInternalFields)
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([subKey, subVal]) => `  - ${subKey}: ${subVal}`)
    .join('\n');

const formatNestedObject = (key: string, value: Record<string, unknown>): string => {
  const entries = Object.entries(value);
  return `${key}:\n${formatObjectEntries(entries)}`;
};

const formatArrayValue = (key: string, value: unknown[]): string => {
  const firstVal = value[0];
  if (typeof firstVal === 'object' && firstVal !== null) {
    const entries = Object.entries(firstVal);
    return `${key}:\n${formatObjectEntries(entries)}`;
  }
  return `${key}: ${value[0]}`;
};

export const formatValue = (value: ValueType): string => {
  if (!value) return '';

  if (Array.isArray(value) && value[0] && isPriceStructure(value[0])) {
    return formatPriceStructure(value[0]);
  }

  if (Array.isArray(value) && value[0] && 'language_tag' in value[0] && 'value' in value[0]) {
    return formatLanguageTaggedValues(value);
  }

  if (Array.isArray(value) && value[0] && typeof value[0] === 'object') {
    const nestedValue = value[0];
    return Object.entries(nestedValue)
      .filter(filterInternalFields)
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([key, val]) => {
        if (val && typeof val === 'object' && !Array.isArray(val)) {
          return formatNestedObject(key, val as Record<string, unknown>);
        }

        if (Array.isArray(val) && val.length > 0) {
          return formatArrayValue(key, val);
        }

        return `${key}: ${val}`;
      })
      .join('\n');
  }

  if (typeof value === 'string') {
    return value
      .split('\n')
      .filter((line) => line.trim())
      .join('\n');
  }

  return '';
};

export const renderValue = (value: ValueType): ReactNode => {
  const formattedValue = formatValue(value);

  const isLongText = formattedValue.length > 100;

  if (formattedValue.includes('\n')) {
    const lines = formattedValue.split('\n');

    return (
      <pre
        data-cy="multiline-content"
        style={{
          margin: 0,
          ...wrapStyles,
          whiteSpace: 'pre-wrap' as const,
        }}
      >
        {renderMultilineText(lines)}
      </pre>
    );
  }

  if (isLongText) {
    return (
      <div style={wrapStyles}>
        <Text type="b300">{formattedValue}</Text>
      </div>
    );
  }

  return <Text type="b300">{formattedValue}</Text>;
};

const renderMultilineText = (lines: string[]): ReactNode => {
  return lines.map((line, index) => {
    if (isLanguageHeader(line)) {
      return (
        <div
          data-cy={`language-header-${index}`}
          // biome-ignore lint/suspicious/noArrayIndexKey: legacy code
          key={index}
          style={{
            marginTop: index > 0 ? '1.5rem' : 0,
            paddingTop: index > 0 ? '1rem' : 0,
          }}
        >
          <Text type="b400">{line}</Text>
        </div>
      );
    }
    return (
      <div
        data-cy={`content-line-${index}`}
        // biome-ignore lint/suspicious/noArrayIndexKey: legacy code
        key={index}
        style={{
          marginTop: index > 0 ? '.5rem' : 0,
        }}
      >
        <Text type="b300">{line}</Text>
      </div>
    );
  });
};

const STATUS_MAP: Record<string, STATUSES> = {
  DRAFT_WITH_ACTIVE: STATUSES.DRAFT_WITH_ACTIVE,
  PUBLISH_FAILED: STATUSES.PUBLISH_FAILED,
  PUBLISH_PENDING: STATUSES.PUBLISH_PENDING,
  PUBLISHED: STATUSES.PUBLISHED,
};

/**
 * Renders a status value as a badge.
 * This function is specifically for the status column in the bulk publish table.
 *
 * @param value - The status value to render
 * @returns A React node representing the formatted status as a badge
 */
export const renderStatus = (value: ValueType): ReactNode => {
  const formattedValue = formatValue(value);
  const upperValue = formattedValue.toUpperCase();

  // look up the status in the map, default to DRAFT if not found
  const statusType = STATUS_MAP[upperValue] || STATUSES.DRAFT;

  return <Status type={statusType} />;
};

export const renderAsinLink = (value: ValueType): ReactNode => {
  const asinValue = value as AsinLinkValue;
  if (!asinValue?.asin || !asinValue?.marketplace) return '';

  return (
    <Link href={`/asin/${asinValue.marketplace}/${asinValue.asin}`} rel="noopener noreferrer" target="_blank">
      {asinValue.asin}
    </Link>
  );
};

export const getPricingAttributeDisplayName = (attributeKey: string): string =>
  Object.entries(PRICING_ATTRIBUTES).find(([_, value]) => value === attributeKey)?.[0] || attributeKey;
